import {EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core'

import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Optional,
  ViewChild
} from '@angular/core'
import {NzUploadChangeParam, NzUploadFile} from 'ng-zorro-antd/upload'
import {NotificatorService} from '../../services/notificator.service'
import {HttpClient} from '@angular/common/http'
import {ImageEditorService} from './image.editor.service'
import {assignModalData} from '../../utils/modal-data.util'
import {NZ_MODAL_DATA} from 'ng-zorro-antd/modal'
import {firstValueFrom} from 'rxjs'

type AspectRatioKey = '1:1' | '3:2' | '3:4' | '4:3' | '16:9' | '24:5'

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Point {
  x: number;
  y: number;
}

type DragMode =
  | 'none'
  | 'move'
  | 'resize-nw'
  | 'resize-ne'
  | 'resize-sw'
  | 'resize-se'

@Component({
  selector: 'app-image-editor',
  templateUrl: 'image.editor.component.html',
  styleUrls: ['image.editor.component.css'],
  standalone: false
})
export class ImageEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() previewSize = 300
  @Input() initialRatio: AspectRatioKey
  @Input() minImageWidth: number
  @Input() minImageWidthToResize: number
  @Input() filepath: string
  @Input() base64Src: string
  @Input() tag: string
  // @Output() cropped = new EventEmitter<Blob>()
  // @Output() cropChanged = new EventEmitter<CropRect>()
  @Output() cropChanged = new EventEmitter<{ image: string; width: number; height: number }>()
  @ViewChild('mainCanvas', {static: true})
  mainCanvasRef!: ElementRef<HTMLCanvasElement>

  @ViewChild('previewCanvas', {static: true})
  previewCanvasRef!: ElementRef<HTMLCanvasElement>

  imageX: number
  imageY: number
  outputX: number
  outputY: number

  ratios: { label: AspectRatioKey; value: number }[] = [
    {label: '1:1', value: 1},
    {label: '3:2', value: 3 / 2},
    {label: '3:4', value: 3 / 4},
    {label: '4:3', value: 4 / 3},
    {label: '16:9', value: 16 / 9},
    {label: '24:5', value: 24 / 5}
  ]

  selectedRatio: AspectRatioKey = '1:1'

  canvasWidth = 900
  canvasHeight = 675

  previewWidth = 400
  previewHeight = 300

  private mainCanvas!: HTMLCanvasElement
  private mainCtx!: CanvasRenderingContext2D

  private previewCanvas!: HTMLCanvasElement
  private previewCtx!: CanvasRenderingContext2D

  private image = new Image()
  imageLoaded = false

  /**
   * Params of display image on canvas
   * imageDrawX/Y/W/H — Area, where will draw image
   */
  private imageDrawX = 0
  private imageDrawY = 0
  private imageDrawWidth = 0
  private imageDrawHeight = 0

  cropRect: CropRect = {
    x: 100,
    y: 100,
    width: 200,
    height: 200
  }

  // private readonly minCropWidth = 60
  private minCropWidth = 60
  private readonly handleSize = 12

  private dragMode: DragMode = 'none'
  private dragStartPoint: Point = {x: 0, y: 0}
  private dragStartRect: CropRect | null = null

  get selectedRatioValue(): number {
    return this.ratios.find(r => r.label === this.selectedRatio)?.value ?? 1
  }

  onRatioChange(ratio: AspectRatioKey): void {
    this.selectedRatio = ratio

    if (!this.imageLoaded) {
      return
    }

    this.rebuildCropRectByAspect()
    this.redraw()
  }

  private async readFromHttp(url: string): Promise<Blob> {
    return await firstValueFrom(this.http.get(url, {responseType: 'blob'}))
  }

  // onFileSelected(event: Event): void {
  onFileSelected(event: { file: NzUploadFile, type: string }, externalFile?: string | Blob, base64?: string): void {
    // console.log('chosen file: ', this.minImageWidth, this.minImageWidthToResize)
    let file = null
    if (event) {
      if (event.type === 'start') {
        file = event.file!.originFileObj!
      } else {
        return
      }
    } else if (externalFile) {
      file = externalFile
    } else if (base64) {
      this.image.src = base64
      this.image.onload = () => {
        this.imageLoaded = true
        this.imageX = this.image.width
        this.imageY = this.image.height
        this.calculateImagePlacement()
        this.createInitialCropRect()
        this.redraw()
        setTimeout(() => {
          this.onCanvasMouseUp()
        }, 128)
      }
      return
    }

    if (!file) {
      return
    }

    this.IES.uploadImage(event, {
      minSize: this.minImageWidth,
      minSizeToResize: this.minImageWidthToResize
    }).then((img: HTMLImageElement) => {
      // console.log('UPLOADED: ', img)
      this.image.onload = () => {
        this.imageLoaded = true
        this.calculateImagePlacement()
        this.createInitialCropRect()
        this.redraw()
        setTimeout(() => {
          this.onCanvasMouseUp()
        }, 128)
      }
      this.image.src = img.src
      this.imageX = img.width
      this.imageY = img.height
    }, error => {
      console.log('REJECTED', error)
    })

    // const reader = new FileReader()
    // reader.onload = () => {
    //   const result = reader.result
    //   if (typeof result !== 'string') {
    //     return
    //   }
    //
    //   // Create temp image
    //   const tempImage = new Image()
    //   tempImage.onload = () => {
    //     if (tempImage.width < this.minImageWidth) {
    //       this.notify.createModalNotification('Incompatible size',
    //         `You Uploaded image with size: ${tempImage.width}x${tempImage.height}px. but it's too small for this image-type.\nMinimal image width should be ', ${this.minImageWidth}px)`,
    //         'error')
    //       return
    //     }
    //
    //     this.image.onload = () => {
    //       this.imageLoaded = true
    //       this.calculateImagePlacement()
    //       this.createInitialCropRect()
    //       this.redraw()
    //       setTimeout(() => {
    //         this.onCanvasMouseUp()
    //       }, 128)
    //     }
    //     this.image.src = result
    //     this.imageX = this.image.width
    //     this.imageY = this.image.height
    //   }
    //
    //   tempImage.src = result
    // }
    // if (!base64) {
    //   reader.readAsDataURL(file)
    // }
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (!this.imageLoaded) {
      return
    }

    const point = this.getCanvasPoint(event)
    const hit = this.getHitArea(point)

    if (hit === 'outside') {
      this.dragMode = 'none'
      return
    }

    this.dragMode = hit
    this.dragStartPoint = point
    this.dragStartRect = {...this.cropRect}
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.imageLoaded) {
      return
    }

    const point = this.getCanvasPoint(event)

    if (this.dragMode === 'none' || !this.dragStartRect) {
      this.updateCursor(point)
      return
    }

    const dx = point.x - this.dragStartPoint.x
    const dy = point.y - this.dragStartPoint.y

    switch (this.dragMode) {
      case 'move':
        this.moveCrop(dx, dy)
        break
      case 'resize-nw':
        this.resizeFromCorner('nw', dx, dy)
        break
      case 'resize-ne':
        this.resizeFromCorner('ne', dx, dy)
        break
      case 'resize-sw':
        this.resizeFromCorner('sw', dx, dy)
        break
      case 'resize-se':
        this.resizeFromCorner('se', dx, dy)
        break
    }

    this.redraw()
  }

  onCanvasMouseUp(): void {
    this.dragMode = 'none'
    this.dragStartRect = null

    const output = this.getCroppedImageDataUrl('image/jpeg', 0.92)
    if (!output) {
      return
    }
    this.cropChanged.emit({
      image: output,
      width: this.outputX,
      height: this.outputY
    })
  }

  onCanvasMouseLeave(): void {
    this.dragMode = 'none'
    this.dragStartRect = null
    this.mainCanvas.style.cursor = 'default'
  }

  /**
   * CROP RESULT CAN TAKE HERE
   */
  getCroppedImageDataUrl(type: 'image/png' | 'image/jpeg' = 'image/jpeg', quality = 0.92): string | null {
    if (!this.imageLoaded) {
      return null
    }

    const sourceRect = this.getSourceCropRectInImagePixels()
    if (!sourceRect) {
      return null
    }

    const outputCanvas = document.createElement('canvas')
    outputCanvas.width = Math.round(sourceRect.width)
    outputCanvas.height = Math.round(sourceRect.height)

    const ctx = outputCanvas.getContext('2d')
    if (!ctx) {
      return null
    }

    ctx.drawImage(
      this.image,
      sourceRect.x,
      sourceRect.y,
      sourceRect.width,
      sourceRect.height,
      0,
      0,
      outputCanvas.width,
      outputCanvas.height
    )

    return outputCanvas.toDataURL(type, quality)
  }

  /**
   * Get blob for upload and save
   */
  async getCroppedImageBlob(
    type: 'image/png' | 'image/jpeg' = 'image/jpeg',
    quality = 1
  ): Promise<Blob | null> {
    if (!this.imageLoaded) {
      return null
    }

    const sourceRect = this.getSourceCropRectInImagePixels()
    if (!sourceRect) {
      return null
    }

    const outputCanvas = document.createElement('canvas')
    outputCanvas.width = Math.round(sourceRect.width)
    outputCanvas.height = Math.round(sourceRect.height)

    const ctx = outputCanvas.getContext('2d')
    if (!ctx) {
      return null
    }

    ctx.drawImage(
      this.image,
      sourceRect.x,
      sourceRect.y,
      sourceRect.width,
      sourceRect.height,
      0,
      0,
      outputCanvas.width,
      outputCanvas.height
    )

    return new Promise(resolve => {
      outputCanvas.toBlob(blob => resolve(blob), type, quality)
    })
  }

  downloadCroppedImage(): void {
    const dataUrl = this.getCroppedImageDataUrl('image/jpeg', 0.92)
    if (!dataUrl) {
      return
    }

    const link = document.createElement('a')
    link.href = dataUrl
    link.download = 'cropped-image.jpeg'
    link.click()
  }

  // async getOutput() {
  //   const dataUrl = await this.getCroppedImageDataUrl('image/png')
  //   if (!dataUrl) {
  //     return
  //   }
  //   return dataUrl
  // }


  // =========================
  // INTERNAL LOGIC
  // =========================

  private calculateImagePlacement(): void {
    const canvasRatio = this.canvasWidth / this.canvasHeight
    const imageRatio = this.image.width / this.image.height

    const margin = 20

    if (imageRatio > canvasRatio) {
      console.log('___R1')
      this.imageDrawWidth = this.canvasWidth - margin
      this.imageDrawHeight = (this.canvasWidth / imageRatio) - margin
      this.imageDrawX = 0 + margin / 2
      this.imageDrawY = (this.canvasHeight - this.imageDrawHeight) / 2
    } else {
      console.log('___R2')
      this.imageDrawHeight = this.canvasHeight - margin
      this.imageDrawWidth = (this.canvasHeight * imageRatio) - margin
      this.imageDrawY = 0 + margin / 2
      this.imageDrawX = (this.canvasWidth - this.imageDrawWidth) / 2
    }

    this.minCropWidth = Math.ceil(this.minImageWidth * Math.round(this.imageDrawWidth) / this.image.width)
    console.log(this.minCropWidth, this.minImageWidth, this.imageDrawWidth, this.image.width)
  }

  private createInitialCropRect(): void {
    const ratio = this.selectedRatioValue


    const maxW = this.imageDrawWidth // * 0.97
    const maxH = this.imageDrawHeight // * 0.97

    // console.log('image details', {
    //   x: this.image.width,
    //   y: this.image.height,
    //   drawX: this.imageDrawWidth,
    //   drawY: this.imageDrawHeight,
    //   maxX: maxW,
    //   maxY: maxH,
    //   limX: this.minCropWidth
    // })

    // let width = maxW
    // let height = width / ratio
    let width = maxW
    let height = width / ratio
    // let height = this.imageDrawHeight
    // let width = this.image.width / ratio

    if (height > maxH) {
      height = maxH
      width = height * ratio
    }

    this.cropRect = {
      x: this.imageDrawX + (this.imageDrawWidth - width) / 2,
      y: this.imageDrawY + (this.imageDrawHeight - height) / 2,
      width,
      height
    }

    this.clampCropRect()
  }

  private rebuildCropRectByAspect(): void {
    const ratio = this.selectedRatioValue
    const centerX = this.cropRect.x + this.cropRect.width / 2
    const centerY = this.cropRect.y + this.cropRect.height / 2

    let width = this.cropRect.width
    let height = width / ratio

    if (height > this.imageDrawHeight) {
      height = this.imageDrawHeight * 0.9
      width = height * ratio
    }

    if (width > this.imageDrawWidth) {
      width = this.imageDrawWidth * 0.9
      height = width / ratio
    }

    this.cropRect = {
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height
    }

    this.clampCropRect()
  }

  private moveCrop(dx: number, dy: number): void {
    if (!this.dragStartRect) {
      return
    }

    this.cropRect.x = this.dragStartRect.x + dx
    this.cropRect.y = this.dragStartRect.y + dy

    this.clampCropRect()
  }

  private resizeFromCorner(
    corner: 'nw' | 'ne' | 'sw' | 'se',
    dx: number,
    dy: number
  ): void {
    if (!this.dragStartRect) {
      return
    }

    const start = this.dragStartRect
    const ratio = this.selectedRatioValue

    const anchor = this.getOppositeAnchor(start, corner)

    // console.log('resize: ', {start, ratio, anchor})
    console.log('resize: ', {x: this.outputX, y: this.outputY})

    // if (this.outputX <= this.minImageWidth) {
    //   return
    // }

    /**
     * For fixed aspect ratio - take ONE control param
     * For right corners - width calculate to right
     * For left corners - to left
     */
    let newWidth: number

    // minImageWidth

    switch (corner) {
      case 'se':
        newWidth = Math.max(this.minCropWidth, pointDistanceX(anchor.x, start.x + start.width + dx))
        break
      case 'ne':
        newWidth = Math.max(this.minCropWidth, pointDistanceX(anchor.x, start.x + start.width + dx))
        break
      case 'sw':
        newWidth = Math.max(this.minCropWidth, pointDistanceX(anchor.x, start.x + dx))
        break
      case 'nw':
        newWidth = Math.max(this.minCropWidth, pointDistanceX(anchor.x, start.x + dx))
        break
    }

    let newHeight = newWidth / ratio

    const maxByBounds = this.getMaxSizeFromAnchor(anchor, corner, ratio)
    if (newWidth > maxByBounds.width) {
      newWidth = maxByBounds.width
      newHeight = newWidth / ratio
    }

    switch (corner) {
      case 'se':
        this.cropRect = {
          x: anchor.x,
          y: anchor.y,
          width: newWidth,
          height: newHeight
        }
        break

      case 'ne':
        this.cropRect = {
          x: anchor.x,
          y: anchor.y - newHeight,
          width: newWidth,
          height: newHeight
        }
        break

      case 'sw':
        this.cropRect = {
          x: anchor.x - newWidth,
          y: anchor.y,
          width: newWidth,
          height: newHeight
        }
        break

      case 'nw':
        this.cropRect = {
          x: anchor.x - newWidth,
          y: anchor.y - newHeight,
          width: newWidth,
          height: newHeight
        }
        break
    }

    this.clampCropRect()
  }


  private getOppositeAnchor(rect: CropRect, corner: 'nw' | 'ne' | 'sw' | 'se'): Point {
    switch (corner) {
      case 'nw':
        return {x: rect.x + rect.width, y: rect.y + rect.height}
      case 'ne':
        return {x: rect.x, y: rect.y + rect.height}
      case 'sw':
        return {x: rect.x + rect.width, y: rect.y}
      case 'se':
        return {x: rect.x, y: rect.y}
    }
  }

  private getMaxSizeFromAnchor(
    anchor: Point,
    corner: 'nw' | 'ne' | 'sw' | 'se',
    ratio: number
  ): { width: number; height: number } {
    let maxWidthByX = 0
    let maxHeightByY = 0

    switch (corner) {
      case 'se':
        maxWidthByX = this.imageDrawX + this.imageDrawWidth - anchor.x
        maxHeightByY = this.imageDrawY + this.imageDrawHeight - anchor.y
        break
      case 'ne':
        maxWidthByX = this.imageDrawX + this.imageDrawWidth - anchor.x
        maxHeightByY = anchor.y - this.imageDrawY
        break
      case 'sw':
        maxWidthByX = anchor.x - this.imageDrawX
        maxHeightByY = this.imageDrawY + this.imageDrawHeight - anchor.y
        break
      case 'nw':
        maxWidthByX = anchor.x - this.imageDrawX
        maxHeightByY = anchor.y - this.imageDrawY
        break
    }

    let width = maxWidthByX
    let height = width / ratio

    if (height > maxHeightByY) {
      height = maxHeightByY
      width = height * ratio
    }

    return {width, height}
  }

  private clampCropRect(): void {
    const minX = this.imageDrawX
    const minY = this.imageDrawY
    const maxX = this.imageDrawX + this.imageDrawWidth
    const maxY = this.imageDrawY + this.imageDrawHeight

    if (this.cropRect.width > this.imageDrawWidth) {
      this.cropRect.width = this.imageDrawWidth
      this.cropRect.height = this.cropRect.width / this.selectedRatioValue
    }

    if (this.cropRect.height > this.imageDrawHeight) {
      this.cropRect.height = this.imageDrawHeight
      this.cropRect.width = this.cropRect.height * this.selectedRatioValue
    }

    if (this.cropRect.x < minX) {
      this.cropRect.x = minX
    }

    if (this.cropRect.y < minY) {
      this.cropRect.y = minY
    }

    if (this.cropRect.x + this.cropRect.width > maxX) {
      this.cropRect.x = maxX - this.cropRect.width
    }

    if (this.cropRect.y + this.cropRect.height > maxY) {
      this.cropRect.y = maxY - this.cropRect.height
    }
  }

  private redraw(): void {
    this.clearMainCanvas()

    if (this.imageLoaded) {
      this.drawImage()
      this.drawOverlay()
      this.drawCropFrame()
      this.drawHandles()
      this.drawRuleOfThirds()
      this.drawPreview()
    } else {
      this.drawEmptyState()
      this.clearPreviewCanvas()
    }
  }

  private clearMainCanvas(): void {
    this.mainCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
  }

  private drawImage(): void {
    this.mainCtx.drawImage(
      this.image,
      this.imageDrawX,
      this.imageDrawY,
      this.imageDrawWidth,
      this.imageDrawHeight
    )
  }

  // Overlay color in front of image
  private drawOverlay(): void {
    this.mainCtx.save()
    this.mainCtx.fillStyle = 'rgba(0, 0, 0, 0.45)'

    const x = Math.round(this.cropRect.x)
    const y = Math.round(this.cropRect.y)
    const w = Math.round(this.cropRect.width)
    const h = Math.round(this.cropRect.height)

    // console.log('cv-dim: ', {
    //   x, y, w, h,
    //   cw: this.canvasWidth,
    //   ch: this.canvasHeight
    // })

    // up / down / left / right
    this.mainCtx.fillRect(0, 0, this.canvasWidth, y + 0.04)
    this.mainCtx.fillRect(0, y + h, this.canvasWidth, this.canvasHeight - (y + h))
    this.mainCtx.fillRect(0, y, x, h)
    this.mainCtx.fillRect(x + w, y, this.canvasWidth - (x + w), h)

    this.mainCtx.restore()
  }

  // private drawOverlay(): void {
  //   this.mainCtx.save()
  //   this.mainCtx.fillStyle = 'rgba(0, 0, 0, 0.75)'
  //   this.mainCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
  //
  //   this.mainCtx.clearRect(
  //     this.cropRect.x,
  //     this.cropRect.y,
  //     this.cropRect.width,
  //     this.cropRect.height
  //   )
  //   this.mainCtx.restore()
  // }

  private drawCropFrame(): void {
    this.mainCtx.save()
    this.mainCtx.strokeStyle = '#fff'
    this.mainCtx.lineWidth = 2
    this.mainCtx.strokeRect(
      this.cropRect.x,
      this.cropRect.y,
      this.cropRect.width,
      this.cropRect.height
    )
    this.mainCtx.restore()
  }

  private drawHandles(): void {
    const handles = this.getHandlePoints()

    this.mainCtx.save()
    this.mainCtx.fillStyle = '#ffffff'
    // this.mainCtx.fillStyle = '#00ff00'
    // this.mainCtx.fillStyle = 'rgba(0, 0, 0, 0.15)'

    handles.forEach(handle => {
      this.mainCtx.fillRect(
        handle.x - this.handleSize / 2,
        handle.y - this.handleSize / 2,
        this.handleSize,
        this.handleSize
      )
    })

    this.mainCtx.restore()
  }

  // Add Golg rules to cropper-tool
  private drawRuleOfThirds(): void {
    const x1 = this.cropRect.x + this.cropRect.width / 3
    const x2 = this.cropRect.x + (this.cropRect.width / 3) * 2
    const y1 = this.cropRect.y + this.cropRect.height / 3
    const y2 = this.cropRect.y + (this.cropRect.height / 3) * 2

    this.mainCtx.save()
    this.mainCtx.strokeStyle = 'rgba(255,255,0,0.75)'
    // this.mainCtx.fillStyle = 'rgba(0, 0, 0, 0.15)'
    this.mainCtx.lineWidth = 1

    this.mainCtx.beginPath()
    this.mainCtx.moveTo(x1, this.cropRect.y)
    this.mainCtx.lineTo(x1, this.cropRect.y + this.cropRect.height)

    this.mainCtx.moveTo(x2, this.cropRect.y)
    this.mainCtx.lineTo(x2, this.cropRect.y + this.cropRect.height)

    this.mainCtx.moveTo(this.cropRect.x, y1)
    this.mainCtx.lineTo(this.cropRect.x + this.cropRect.width, y1)

    this.mainCtx.moveTo(this.cropRect.x, y2)
    this.mainCtx.lineTo(this.cropRect.x + this.cropRect.width, y2)

    this.mainCtx.stroke()
    this.mainCtx.restore()
  }

  private drawPreview(): void {
    const sourceRect = this.getSourceCropRectInImagePixels()
    if (!sourceRect) {
      this.clearPreviewCanvas()
      return
    }

    this.previewCtx.clearRect(0, 0, this.previewWidth, this.previewHeight)

    const cropRatio = this.cropRect.width / this.cropRect.height
    let drawW = this.previewWidth
    let drawH = drawW / cropRatio

    if (drawH > this.previewHeight) {
      drawH = this.previewHeight
      drawW = drawH * cropRatio
    }

    const offsetX = (this.previewWidth - drawW) / 2
    const offsetY = (this.previewHeight - drawH) / 2

    this.previewCtx.drawImage(
      this.image,
      sourceRect.x,
      sourceRect.y,
      sourceRect.width,
      sourceRect.height,
      offsetX,
      offsetY,
      drawW,
      drawH
    )
    this.outputX = Math.round(sourceRect.width)
    this.outputY = Math.round(sourceRect.height)

    // return outputCanvas.toDataURL(type, quality)
    // console.log('redraw: ', {
    //   img: this.image,
    //   x: sourceRect.x,
    //   y: sourceRect.y,
    //   w: sourceRect.width,
    //   h: sourceRect.height,
    //   offsetX,
    //   offsetY,
    //   drawW,
    //   drawH
    // })
  }

  private getSourceCropRectInImagePixels(): CropRect | null {
    if (!this.imageLoaded) {
      return null
    }

    const scaleX = this.image.width / this.imageDrawWidth
    const scaleY = this.image.height / this.imageDrawHeight

    const sourceX = (this.cropRect.x - this.imageDrawX) * scaleX
    const sourceY = (this.cropRect.y - this.imageDrawY) * scaleY
    const sourceW = this.cropRect.width * scaleX
    const sourceH = this.cropRect.height * scaleY

    return {
      x: clamp(sourceX, 0, this.image.width),
      y: clamp(sourceY, 0, this.image.height),
      width: clamp(sourceW, 1, this.image.width),
      height: clamp(sourceH, 1, this.image.height)
    }
  }

  private clearPreviewCanvas(): void {
    this.previewCtx.clearRect(0, 0, this.previewWidth, this.previewHeight)
  }

  private drawEmptyState(): void {
    this.mainCtx.save()
    this.mainCtx.fillStyle = '#f3f4f6'
    this.mainCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

    this.mainCtx.fillStyle = '#6b7280'
    this.mainCtx.font =
      '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    this.mainCtx.textAlign = 'center'
    this.mainCtx.fillText(
      'Upload image to begin crop',
      this.canvasWidth / 2,
      this.canvasHeight / 2
    )

    this.mainCtx.restore()
  }

  private getCanvasPoint(event: MouseEvent): Point {
    const rect = this.mainCanvas.getBoundingClientRect()
    const scaleX = this.mainCanvas.width / rect.width
    const scaleY = this.mainCanvas.height / rect.height

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    }
  }

  private getHandlePoints(): Point[] {
    return [
      {x: this.cropRect.x, y: this.cropRect.y},
      {x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y},
      {x: this.cropRect.x, y: this.cropRect.y + this.cropRect.height},
      {x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y + this.cropRect.height}
    ]
  }

  private getHitArea(point: Point): DragMode | 'outside' {
    const [nw, ne, sw, se] = this.getHandlePoints()

    if (this.isPointInsideHandle(point, nw)) {
      return 'resize-nw'
    }
    if (this.isPointInsideHandle(point, ne)) {
      return 'resize-ne'
    }
    if (this.isPointInsideHandle(point, sw)) {
      return 'resize-sw'
    }
    if (this.isPointInsideHandle(point, se)) {
      return 'resize-se'
    }

    if (this.isPointInsideRect(point, this.cropRect)) {
      return 'move'
    }

    return 'outside'
  }

  private updateCursor(point: Point): void {
    const area = this.getHitArea(point)

    switch (area) {
      case 'resize-nw':
      case 'resize-se':
        this.mainCanvas.style.cursor = 'nwse-resize'
        return
      case 'resize-ne':
      case 'resize-sw':
        this.mainCanvas.style.cursor = 'nesw-resize'
        return
      case 'move':
        this.mainCanvas.style.cursor = 'move'
        return
      default:
        this.mainCanvas.style.cursor = 'default'
    }
  }

  private isPointInsideHandle(point: Point, handleCenter: Point): boolean {
    return (
      point.x >= handleCenter.x - this.handleSize &&
      point.x <= handleCenter.x + this.handleSize &&
      point.y >= handleCenter.y - this.handleSize &&
      point.y <= handleCenter.y + this.handleSize
    )
  }

  private isPointInsideRect(point: Point, rect: CropRect): boolean {
    return (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    )
  }


  constructor(
    private IES: ImageEditorService,
    private notify: NotificatorService,
    private http: HttpClient,
    @Optional() @Inject(NZ_MODAL_DATA) modalData?: Partial<ImageEditorComponent>
  ) {
    assignModalData(this, modalData)
  }

  async loadExternalFile(filepath: string) {
    const blob = await this.readFromHttp(filepath)
    this.onFileSelected(null, blob)
  }

  ngAfterViewInit(): void {
    console.log('editor: ', this)

    // init min size
    this.selectedRatio = this.initialRatio

    this.mainCanvas = this.mainCanvasRef.nativeElement
    this.previewCanvas = this.previewCanvasRef.nativeElement

    const mainCtx = this.mainCanvas.getContext('2d')
    const previewCtx = this.previewCanvas.getContext('2d')

    if (!mainCtx || !previewCtx) {
      throw new Error('Canvas context is not available')
    }

    this.mainCtx = mainCtx
    this.previewCtx = previewCtx

    this.mainCanvas.width = this.canvasWidth
    this.mainCanvas.height = this.canvasHeight

    this.previewCanvas.width = this.previewWidth
    this.previewCanvas.height = this.previewHeight

    this.redraw()

    // try load file from input
    if (this.filepath && this.filepath.includes('img/')) {
      console.log('filepath detected')
      const origin = window.location.origin
      this.loadExternalFile((origin + this.filepath))
    } else if (this.base64Src) {
      console.log('base64 detected')
      this.onFileSelected(null, null, this.base64Src)
    }

    // subscribe on global change image
    this.IES.subscriptOnFile$.subscribe(newfilepath => {
      if (newfilepath) {
        console.log('update file: ', {newfilepath})
        this.onFileSelected(null, null, newfilepath)
      }
    })
  }

  ngOnInit() {
  }

  ngOnDestroy() {
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function pointDistanceX(a: number, b: number): number {
  return Math.abs(b - a)
}
